import React, { useState, useRef } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './ProfilePictureUpload.css';

interface ProfilePictureUploadProps {
  currentImageUrl?: string | null;
  memberInitials: string;
  onUploadSuccess: (newUrl: string) => void;
  onDeleteSuccess: () => void;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentImageUrl,
  memberInitials,
  onUploadSuccess,
  onDeleteSuccess,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compress phone-camera photos (3-8 MB) down to ~200 KB before upload.
  // Server resizes to 400x400 anyway, but the raw upload was stalling the
  // gunicorn worker past --timeout (SIGKILL). Mirrors compressImage() in
  // BridgeClubUploadReceipt.tsx (commit 9b281d4e).
  const compressImage = (file: File): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const maxWidth = 1200;
          const ratio = Math.min(maxWidth / img.width, 1);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas not supported'));
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Image compression failed'));
                return;
              }
              resolve(new File([blob], 'profile.jpg', { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.85
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = reader.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsUploading(true);

    try {
      const compressed = await compressImage(file);
      const result = await bridgeClubAPI.uploadProfilePicture(compressed);
      if (result.profile_picture_url) {
        onUploadSuccess(result.profile_picture_url);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove your profile picture?')) return;

    setError(null);
    try {
      await bridgeClubAPI.deleteProfilePicture();
      onDeleteSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
    }
  };

  return (
    <div className="profile-picture-upload">
      <div className="profile-picture-container">
        {currentImageUrl ? (
          <img
            src={currentImageUrl}
            alt="Profile"
            className="profile-picture"
          />
        ) : (
          <div className="profile-picture-placeholder">
            {memberInitials}
          </div>
        )}

        <button
          className="upload-overlay"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          type="button"
        >
          {isUploading ? '...' : '📷'}
        </button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
      />

      {currentImageUrl && (
        <button
          className="remove-picture-btn"
          onClick={handleDelete}
          type="button"
        >
          Remove photo
        </button>
      )}

      {error && <p className="upload-error">{error}</p>}
    </div>
  );
};

export default ProfilePictureUpload;
